import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import React from 'react';
import CountdownTimer from './CountdownTimer';

export interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  originalPrice?: number;
  discount?: {
    code: string;
    percentage: number;
    expiryDate?: Date;
  };
  image: string;
  imageAlt: string;
  ctaText: string;
  ctaUrl: string;
  ctaUrlWithCoupon?: string;
  isNew?: boolean;
}

interface CourseCardProps {
  course: Course;
  onCtaClick?: (course: Course) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onCtaClick }) => {
  // Verificar si el cupón está vigente
  const isCouponValid = () => {
    if (!course.discount?.expiryDate) return true; // Sin fecha de expiración, siempre válido
    const now = new Date();
    const expiryDate = new Date(course.discount.expiryDate);
    return now <= expiryDate;
  };

  const couponValid = isCouponValid();
  const showDiscount = course.discount && couponValid;

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick(course);
    } else {
      // Usar URL con cupón si está disponible y válido, sino la URL normal
      const targetUrl = (couponValid && course.ctaUrlWithCoupon) ? course.ctaUrlWithCoupon : course.ctaUrl;
      window.open(targetUrl, '_blank', 'noopener noreferrer');
    }
  };

  const hasDiscount = course.originalPrice && course.originalPrice > course.price && couponValid;
  const discountPercentage = hasDiscount 
    ? Math.round(((course.originalPrice! - course.price) / course.originalPrice!) * 100)
    : 0;

  return (
    <Card className="h-full flex flex-col hover:shadow-lg transition-shadow duration-300">
      <CardHeader className="relative p-0">
        <div className="relative">
          <img
            src={course.image}
            alt={course.imageAlt}
            className="w-full h-48 object-cover rounded-t-lg"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            {course.isNew && (
              <Badge variant="secondary" className="bg-green-500 text-white hover:bg-green-600">
                Nuevo
              </Badge>
            )}
            {hasDiscount && (
              <Badge variant="destructive" className="bg-red-500 text-white hover:bg-red-600">
                -{discountPercentage}%
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 p-6">
        <CardTitle className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
          {course.title}
        </CardTitle>
        <CardDescription className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
          {course.description}
        </CardDescription>
        
        <div className="flex items-center gap-2 mb-4">
          {hasDiscount && (
            <span className="text-lg text-gray-400 line-through">
              ${course.originalPrice?.toFixed(2)}
            </span>
          )}
          <span className="text-2xl font-bold text-green-600">
            ${couponValid ? course.price.toFixed(2) : course.originalPrice?.toFixed(2) || course.price.toFixed(2)}
          </span>
        </div>
        
         {showDiscount && (
           <div className="mb-4">
             <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
               <p className="text-sm text-yellow-800">
                 <strong>Cupón:</strong> {course.discount.code} 
                 <span className="ml-2 text-yellow-600">(-{course.discount.percentage}%)</span>
               </p>
             </div>
             
             {course.discount.expiryDate && (
               <CountdownTimer 
                 targetDate={course.discount.expiryDate} 
                 size="small"
                 variant="default"
               />
             )}
           </div>
         )}
      </CardContent>
      
      <CardFooter className="p-6 pt-0">
        <Button 
          onClick={handleCtaClick}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
        >
          {course.ctaText}
          <ExternalLink className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CourseCard;
